import type { ReportGranularity } from "../../../shared/components/reports/types";

/** Who a session was with, as a report groups it. */
export type ReportSubjectKind = "patient" | "couple" | "group";

export type ReportSessionType = "individual" | "couple" | "group";

/** Money arrives as a two-decimal string, or null when no rate applies. */
export type Amount = string | null;

export interface ReportTotals {
    sessions: number;
    finished: number;
    pending: number;
    cancelled_by_patient: number;
    cancelled_by_therapist: number;
    /** Minutes of the sessions that took place. */
    minutes: number;
    /** Distinct patients, couples and groups. */
    subjects: number;
    /** People who came, over the sessions whose attendance is known. */
    attendees: number;
    /** Sessions whose date was moved at least once. */
    rescheduled: number;
    /** What is owed: the held sessions. A cancelled one is worth nothing. */
    amount: Amount;
    amount_finished: Amount;
}

export interface ReportPeriod {
    /** First day of the period, `YYYY-MM-DD`. */
    period: string;
    finished: number;
    pending: number;
    cancelled: number;
    amount: Amount;
}

export interface ReportTypeRow {
    type: ReportSessionType;
    sessions: number;
    amount: Amount;
}

export interface ReportSubjectRow {
    type: ReportSubjectKind;
    id: string;
    /** The patient's or couple's name, the group's own name — or null for an unnamed group. */
    label: string | null;
    member_count: number | null;
    course: string | null;
    session_type: ReportSessionType;
    sessions: number;
    finished: number;
    pending: number;
    cancelled: number;
    amount: Amount;
    /**
     * A training centre's student: modules of their course's plan covered by
     * their mentorings, over the whole course. Absent for anybody else.
     */
    modules_covered?: number | null;
    modules_total?: number | null;
    module_coverage_percent?: number | null;
    /** Who held their sessions, where the report spans several professionals. */
    mentors?: string[];
    /** A patient's values of the centre's own fields, by field id. Absent outside a centre. */
    custom_values?: Record<string, string>;
}

export type ReportCustomFieldType = "text" | "number" | "date" | "select";

/** One of the centre's own patient fields, as a report's column. */
export interface ReportCustomField {
    id: string;
    label: string;
    field_type: ReportCustomFieldType;
}

/** How a statement is downloaded: the fixed-layout PDF, or an Excel workbook. */
export type StatementFormat = "pdf" | "xlsx";

export type ReportSessionStatus = "pending" | "finished" | "cancelled";

export interface ReportSessionRow {
    id: string;
    /** Wall-clock time on the centre's clock, without an offset. */
    date: string;
    subject_type: ReportSubjectKind;
    subject_id: string;
    label: string | null;
    member_count: number | null;
    course: string | null;
    course_id: string | null;
    /** A training centre's course module the mentorship worked on. */
    module: string | null;
    /** The professional credited with it, in a report across several. */
    therapist_id: string | null;
    therapist_name: string | null;
    type: ReportSessionType;
    status: ReportSessionStatus;
    cancelled_by: "patient" | "therapist" | null;
    cancelled_at: string | null;
    /** Who came, of how many: null when nobody could have, or nobody said. */
    attendees: number | null;
    participants: number | null;
    /** Whether its date was moved at least once. */
    rescheduled: boolean;
    /** Only when owed: a held session. */
    amount: Amount;
}

export interface ReportMentorRow {
    therapist_id: string;
    name: string;
    sessions: number;
    individual: number;
    couple: number;
    group: number;
    finished: number;
    pending: number;
    cancelled: number;
    /** Sessions whose date was moved at least once. */
    rescheduled: number;
    /** The sessions that are paid: the held ones. */
    billable: number;
    /** Distinct patients, couples and groups; minutes of the held sessions. */
    subjects: number;
    minutes: number;
    amount: Amount;
}

export interface ReportCourseOption {
    id: string;
    name: string;
}

export interface CourseOverviewRow {
    course_id: string;
    name: string;
    sessions: number;
    finished: number;
    pending: number;
    cancelled: number;
    students: number;
    mentors: number;
    amount: Amount;
}

export interface CourseOverview {
    currency: "EUR";
    has_amounts: boolean;
    courses: CourseOverviewRow[];
}

export interface PractitionerReport {
    currency: "EUR";
    /** Whether any rate applies to any session in the window. */
    has_amounts: boolean;
    totals: ReportTotals;
    periods: ReportPeriod[];
    by_type: ReportTypeRow[];
    subjects: ReportSubjectRow[];
    /** Every session, oldest first. */
    sessions: ReportSessionRow[];
    /** A training centre's report: every mentorship shows course and module. */
    training: boolean;
    /** One row per professional, in a report across several. */
    mentors: ReportMentorRow[];
    /** The centre's own patient fields, in order; empty or absent outside a centre. */
    custom_fields?: ReportCustomField[];
}

export interface ReportSubjectOption {
    type: ReportSubjectKind;
    id: string;
    label: string | null;
    member_count: number | null;
}

export interface ReportFilters {
    /** `YYYY-MM-DD`, inclusive. */
    from: string;
    to: string;
    granularity: ReportGranularity;
    /** `"<kind>:<id>"`, or empty for everybody. */
    subject: string;
    /** A course id, or empty for every course. */
    course: string;
}

/** Whose report: the caller's own, or a centre member's as its administrator sees it. */
export type ReportScope =
    | { kind: "self" }
    | { kind: "member"; centerId: string; therapistId: string }
    /** The whole centre, across its professionals: a student's report. */
    | { kind: "centre"; centerId: string }
    /** One of a training centre's courses, across its mentors. */
    | { kind: "course"; centerId: string; courseId: string };

/** Whether a scope can narrow to a course: anything but a course itself. */
export function canPickCourse(scope: ReportScope): boolean {
    return scope.kind !== "course";
}

export interface ReportQueryParams {
    start: string;
    end: string;
    granularity: ReportGranularity;
    subject_type?: ReportSubjectKind;
    subject_id?: string;
    course_id?: string;
}

const SUBJECT_KINDS: ReportSubjectKind[] = ["patient", "couple", "group"];

export function subjectKey(kind: ReportSubjectKind, id: string): string {
    return `${kind}:${id}`;
}

/** The picker's value back into kind and id; anything malformed is "everybody". */
export function parseSubjectKey(
    key: string
): { subject_type: ReportSubjectKind; subject_id: string } | null {
    const [kind, id] = key.split(":");
    if (!id || !SUBJECT_KINDS.includes(kind as ReportSubjectKind)) return null;
    return { subject_type: kind as ReportSubjectKind, subject_id: id };
}

export function toQueryParams(filters: ReportFilters): ReportQueryParams {
    return {
        start: `${filters.from}T00:00:00`,
        end: `${filters.to}T23:59:59`,
        granularity: filters.granularity,
        ...(parseSubjectKey(filters.subject) ?? {}),
        ...(filters.course ? { course_id: filters.course } : {}),
    };
}

/** `YYYY-MM` of a `YYYY-MM-DD`, for the monthly statement. */
export function monthOf(day: string): string {
    return day.slice(0, 7);
}

/** How a subject is named: its label, or — for an unnamed group — its size. */
export function subjectName(
    subject: { label: string | null; member_count: number | null },
    groupOf: (count: number) => string
): string {
    return subject.label ?? groupOf(subject.member_count ?? 0);
}

/**
 * Where a report's rows lead, for whoever is reading it.
 *
 * A session opens only for the professional who holds it: the reader's own
 * report links every one, a centre's view only those the reader held. A
 * patient, couple or group opens its record in the reader's own report; in
 * a centre's view — whose people are other professionals' — it opens their
 * report across the centre instead, which the administrator may read.
 */
export interface ReportLinks {
    session: (session: Pick<ReportSessionRow, "id" | "therapist_id">) => string | null;
    subject: (kind: ReportSubjectKind, id: string) => string | null;
    mentor: (therapistId: string) => string | null;
}

const RECORD_PATHS: Record<ReportSubjectKind, string> = {
    patient: "/patient",
    couple: "/couple-patient",
    group: "/group-patient",
};

export function reportLinks(scope: ReportScope, readerId: string | undefined, fixedSubject?: string): ReportLinks {
    const own = scope.kind === "self";
    return {
        session: (session) =>
            own || (readerId !== undefined && session.therapist_id === readerId) ? `/session/${session.id}` : null,
        subject: (kind, id) => {
            if (own) return `${RECORD_PATHS[kind]}/${id}`;
            // Already on this person's page: a link to itself says nothing.
            if (fixedSubject === subjectKey(kind, id)) return null;
            return `/center/reports/students/${kind}/${id}`;
        },
        mentor: (therapistId) =>
            scope.kind === "self" ? null : `/center/reports/practitioners/${therapistId}`,
    };
}

/** Whether any row is a student whose course has a plan to cover. */
export function hasModuleCoverage(rows: ReportSubjectRow[]): boolean {
    return rows.some((row) => Boolean(row.modules_total));
}
